import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { UserPlus } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RV_TYPES = [
  ['travel_trailer', 'Travel Trailer'],
  ['fifth_wheel', 'Fifth Wheel'],
  ['class_a', 'Class A'],
  ['class_b', 'Class B'],
  ['class_c', 'Class C'],
  ['toy_hauler', 'Toy Hauler'],
  ['popup', 'Pop-Up'],
];

export const AddLeadModal = ({ open, onOpenChange, onCreated, isDark }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', zipcode: '', rv_type: 'travel_trailer', budget_max: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        budget_max: form.budget_max ? parseInt(form.budget_max, 10) : null,
      };
      await axios.post(`${API}/dealer/add-lead`, payload, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      toast.success(`${form.first_name.trim()} added to your pipeline`);
      setForm({ first_name: '', last_name: '', email: '', phone: '', zipcode: '', rv_type: 'travel_trailer', budget_max: '', notes: '' });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add lead');
    }
    setBusy(false);
  };

  const inputCls = isDark ? 'bg-slate-800 border-slate-700 text-white' : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-md ${isDark ? 'bg-slate-900 border-slate-700 text-white' : ''}`} data-testid="add-lead-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={18} className="text-[#004E86]" /> Add Lead
          </DialogTitle>
          <DialogDescription>
            Manually add a buyer from a call, walk-in, or referral. They land in your pipeline as a new lead.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="First name *" value={form.first_name} onChange={set('first_name')} className={inputCls} data-testid="add-lead-first-name" />
            <Input placeholder="Last name" value={form.last_name} onChange={set('last_name')} className={inputCls} data-testid="add-lead-last-name" />
          </div>
          <Input type="email" placeholder="Email (optional)" value={form.email} onChange={set('email')} className={inputCls} data-testid="add-lead-email" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Phone" value={form.phone} onChange={set('phone')} className={inputCls} data-testid="add-lead-phone" />
            <Input placeholder="ZIP code" value={form.zipcode} onChange={set('zipcode')} maxLength={10} className={inputCls} data-testid="add-lead-zip" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={form.rv_type}
              onChange={set('rv_type')}
              className={`h-10 rounded-md border px-3 text-sm w-full ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'border-input bg-background'}`}
              data-testid="add-lead-rv-type"
            >
              {RV_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input type="number" min="0" placeholder="Max budget ($)" value={form.budget_max} onChange={set('budget_max')} className={inputCls} data-testid="add-lead-budget" />
          </div>
          <Textarea placeholder="Notes (optional) — what are they looking for?" rows={3} value={form.notes} onChange={set('notes')} className={inputCls} data-testid="add-lead-notes" />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="add-lead-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#004E86] hover:bg-[#003a65]" data-testid="add-lead-submit">
              {busy ? 'Adding\u2026' : 'Add Lead'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadModal;
